import { supabase } from '../../client';
import { logError } from '@/lib/sentry';
import { ParsedDeposit, DepositMatch, OrderCandidate, PartialDepositOverviewRow } from '../types/deposit';
import { computeAcceptableAmounts, paidThreshold, shippingConfigFingerprint, ShippingConfig } from '../shippingAmounts';
import { normalizeName } from '../normalizeName';

// deposit_match ⨝ parsed_deposit (RLS가 현재 셀러로 스코프). 정렬은 service에서 client-side.
// ★pending 전량 + settled 최신 1000 캡 2쿼리(codexloop 증분2 R2 #3): 단일 무제한 쿼리는
//   PostgREST 기본 1000행 캡에서 오래된 pending이 settled에 밀려 페이지에서 사라질 수 있다
//   (배지 exact count와 괴리 = 이 기능의 정의 불변식 위반). pending을 배지와 동일 조건으로
//   따로 전량 로드해 불변식을 구조적으로 보장. settled(확정/제외)는 아카이브 성격이라
//   최신 1000 캡 수용(초과분 페이지네이션은 백로그).
export async function findDepositsWithMatch(): Promise<{ deposit: ParsedDeposit; match: DepositMatch }[]> {
  const [pending, settled] = await Promise.all([
    supabase
      .from('deposit_match')
      .select('*, parsed_deposit(*)')
      .is('confirmed_at', null)
      .neq('match_reason', 'seller_rejected'),
    supabase
      .from('deposit_match')
      .select('*, parsed_deposit(*)')
      .or('confirmed_at.not.is.null,match_reason.eq.seller_rejected')
      .order('id', { ascending: false })
      .limit(1000),
  ]);
  if (pending.error) { logError('findDepositsWithMatch.pending', pending.error); throw pending.error; }
  if (settled.error) { logError('findDepositsWithMatch.settled', settled.error); throw settled.error; }
  // 두 쿼리 사이 확정/undo 레이스로 같은 match가 양쪽 스냅샷에 잡힐 수 있다(codexloop R3 #2) —
  // id dedupe, modified_at 큰 쪽(더 나중 상태) 우선(R4: 확정 방향은 settled가, undo 방향은
  // pending이 최신 — 방향 고정 대신 타임스탬프 비교). 역방향(양쪽 누락)은 그 mutation 자체가
  // refresh/realtime을 유발해 다음 로드에서 자가 수습되는 1렌더 transient.
  const byId = new Map<number, { id: number; modified_at: string }>();
  for (const row of [...(pending.data ?? []), ...(settled.data ?? [])] as { id: number; modified_at: string }[]) {
    const prev = byId.get(row.id);
    if (!prev || row.modified_at > prev.modified_at) byId.set(row.id, row);
  }
  return [...byId.values()]
    .filter((row: any) => row.parsed_deposit)
    .map((row: any) => {
      const { parsed_deposit, ...match } = row;
      return { deposit: parsed_deposit as ParsedDeposit, match: match as DepositMatch };
    });
}

function summarizeItems(items: { item_name: string }[]): string {
  if (!items || items.length === 0) return '품목 없음';
  if (items.length === 1) return items[0].item_name;
  return `${items[0].item_name} 외 ${items.length - 1}건`;
}

// confirmedSums === null = 합계 로드 실패(degrade): 부분 입금 주문은 expectedAmounts []·remaining null로
// 매핑돼 이번 로드에서 후보/배지에서 자연 제외(보수적 — 잔액 과대평가로 미지정처럼 취급되는 오염 차단).
// 미지정 주문은 합계 맵과 무관해 영향 없음(엣지 Task 4 sumsLoadFailed와 동일 시맨틱).
function mapOrdersToCandidates(orders: any[], config: ShippingConfig, confirmedSums: Map<number, number> | null): OrderCandidate[] {
  // 지문 운반 계약(스펙 §3.3): 임계값(paidThreshold)을 계산한 바로 그 config 스냅샷으로 호출당 1회
  // 계산해 각 후보에 부착 — 액션(확정/undo)은 이 행 페이로드의 지문을 전달한다(액션 시점 재계산 금지).
  const fp = shippingConfigFingerprint(config);
  return orders.map((o: any) => {
    const override = o.shipping_fee_override ?? null;
    const threshold = paidThreshold(o.total_price, override, config);
    const isPartial = o.status === '부분 입금';
    const confirmedSum = isPartial ? (confirmedSums?.get(o.id) ?? 0) : 0;
    const remaining = isPartial && confirmedSums !== null ? threshold - confirmedSum : null;
    return {
      id: o.id,
      nickname: o.nickname,
      order_date: o.order_date,
      created_at: o.created_at,
      total_price: o.total_price,
      status: o.status,
      items_summary: summarizeItems(o.order_item ?? []),
      shippingFeeOverride: override,
      // 부분 입금: 잔액 1값(잔액≤0이면 빈 배열 = 후보 제외, 스펙 §6 방어). 미지정: 기존 2값.
      expectedAmounts: isPartial
        ? (remaining !== null && remaining > 0 ? [remaining] : [])
        : computeAcceptableAmounts(o.total_price, override, config),
      paidThreshold: threshold,
      confirmedSum,
      remainingAmount: remaining,
      settingsFingerprint: fp,
      paidAt: o.paid_at ?? null,
    };
  });
}

// 주문별 확정 입금 합계(잔액 파생용). ★user_id 명시 필터 = 다른 후보 쿼리와 동일 교차노출 방어.
// 실패 시 throw하지 않고 null로 degrade — mapOrdersToCandidates가 부분 입금 주문만 이번 로드에서
// 후보 제외(보수적: 빈 Map이면 confirmedSum=0으로 잔액이 완납액으로 과대평가돼 미지정처럼 오염),
// 미지정 매칭은 계속(엣지 sumsLoadFailed와 동일 시맨틱). orderIds 빈 배열 = 정상 경로 빈 Map(왕복 없음).
export async function getConfirmedDepositSums(orderIds: number[], userId: string): Promise<Map<number, number> | null> {
  const sums = new Map<number, number>();
  if (orderIds.length === 0) return sums;
  const { data, error } = await supabase
    .from('deposit_match')
    .select('matched_order_id, parsed_deposit(amount)')
    .eq('user_id', userId)
    .in('matched_order_id', orderIds)
    .not('confirmed_at', 'is', null);
  if (error) { logError('getConfirmedDepositSums', error); return null; }
  // PostgREST 기본 row cap(1000)에 걸리면 합계가 잘려 과소집계 → 잔액 과대표시 → 허위 정확일치
  // 추천이 될 수 있다. limit 미지정이 기본 1000이라 length===1000은 truncation "가능성"이지만
  // fail-closed로 취급(codexloop 분할 R2 #8): degrade 경로 재사용해 부분 입금 후보만 보수적 제외.
  if ((data ?? []).length >= 1000) {
    logError('getConfirmedDepositSums.truncated', new Error(`possible 1000-row cap truncation for ${orderIds.length} orderIds`));
    return null;
  }
  // unknown 경유 캐스트: Database 제네릭 미사용이라 postgrest-js가 parsed_deposit 임베드를 배열로
  // 추론(findDepositsWithMatch의 기존 any 관례와 동일 원인) — 런타임은 deposit_match→parsed_deposit이
  // 다대일이라 단일 객체. unknown 경유로 no-explicit-any 없이 실제 런타임 shape로 재단언.
  for (const row of (data ?? []) as unknown as { matched_order_id: number | null; parsed_deposit: { amount: number } | null }[]) {
    if (row.matched_order_id == null || row.parsed_deposit == null) continue;
    sums.set(row.matched_order_id, (sums.get(row.matched_order_id) ?? 0) + row.parsed_deposit.amount);
  }
  return sums;
}

// id 배열로 주문 조회. ★user_id 명시 필터 = RLS와 무관하게 항상 본인 주문만(방어).
// (디버그 RLS 정책 등으로 RLS가 느슨해도 입금매칭이 남의 주문을 후보로 끌어오지 않도록.)
export async function findOrdersByIds(ids: number[], config: ShippingConfig, userId: string): Promise<OrderCandidate[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('order')
    .select('id, nickname, order_date, created_at, total_price, status, shipping_fee_override, paid_at, order_item(item_name)')
    .eq('user_id', userId)
    .in('id', ids);
  if (error) { logError('findOrdersByIds', error); throw error; }
  const rows = data ?? [];
  const partialIds = rows.filter((o) => o.status === '부분 입금').map((o) => o.id);
  const sums = await getConfirmedDepositSums(partialIds, userId);
  return mapOrdersToCandidates(rows, config, sums);
}

// 미지정 주문을 입금일 윈도우로 조회(공용 raw fetch) — findUnassignedOrdersInWindow(공개, 필터 후 후보만)와
// findReferenceOrdersInWindow(캡 감지용 rawCount 필요) 양쪽이 공유. 필터 전/후 카운트를 함께 반환해
// 캡 감지가 post-filter 길이에 속지 않게 한다(codexloop 구현 R1 F3, 아래 findReferenceOrdersInWindow 참고).
// ★user_id 명시 필터 = RLS와 무관하게 항상 본인 주문만(방어, findOrdersByIds 동일 이유).
async function fetchUnassignedOrdersRaw(
  fromDate: string, toDate: string, config: ShippingConfig, userId: string
): Promise<{ candidates: OrderCandidate[]; rawCount: number }> {
  const { data, error } = await supabase
    .from('order')
    .select('id, nickname, order_date, created_at, total_price, status, shipping_fee_override, paid_at, order_item(item_name)')
    .eq('user_id', userId)
    .in('status', ['미지정', '부분 입금'])   // 분할입금: 부분 입금 주문도 후보 풀(스펙 §4.1)
    .gte('order_date', fromDate)
    .lte('order_date', toDate)
    // 결정적 순서(수동연결 picker의 동률 정렬 안정화). 매처/큐 경로는 금액·닉네임 필터·카운트라
    // 매칭 결과(상태·건수)는 불변 — 동률일 때의 표시 순서만 결정적으로 고정된다(R2 M7).
    .order('order_date', { ascending: false })
    .order('id', { ascending: false });
  if (error) { logError('findUnassignedOrdersInWindow', error); throw error; }
  const rows = data ?? [];
  const partialIds = rows.filter((o) => o.status === '부분 입금').map((o) => o.id);
  const sums = await getConfirmedDepositSums(partialIds, userId);
  const candidates = mapOrdersToCandidates(rows, config, sums);
  return {
    // 잔액≤0 또는 합계 로드 실패(degrade)로 허용액이 빈 부분 입금 주문은 후보/픽커에서 완전 제외
    // (빈 허용액이 amber·픽커로 새면 total_price 폴백 표시가 오해를 부름 — codexloop 분할 R1 #5)
    candidates: candidates.filter((c) => c.status !== '부분 입금' || c.expectedAmounts.length > 0),
    // 캡 감지 기준(rawCount) = 위 필터 전 원본 행 수. rows.length가 정답 — candidates.length를 쓰면
    // 부분 입금 제외로 줄어든 길이가 1000행 캡을 가려버린다(findReferenceOrdersInWindow가 이 값을 사용).
    rawCount: rows.length,
  };
}

// 미지정 주문을 입금일 윈도우로 조회(금액 필터는 service에서 expectedAmounts로). shipping_fee_override 포함.
// 외부 계약(반환 = 필터 후 후보 배열만)은 기존과 바이트 동일 — 내부만 fetchUnassignedOrdersRaw로 위임.
export async function findUnassignedOrdersInWindow(
  fromDate: string, toDate: string, config: ShippingConfig, userId: string
): Promise<OrderCandidate[]> {
  const { candidates } = await fetchUnassignedOrdersRaw(fromDate, toDate, config, userId);
  return candidates;
}

// 최신 member_setting의 배송설정 (정산 화면과 동일 기준).
// ★user_id 명시 필터 = limit(1) 패턴이라 RLS가 느슨해지면 남의 배송설정을 집어 금액 계산이
//   틀어질 수 있어 항상 본인 것만(방어, order 후보 쿼리 동일 이유).
export async function getShippingConfig(userId: string): Promise<ShippingConfig> {
  const { data, error } = await supabase
    .from('member_setting')
    .select('shipping_fee, free_shipping_threshold, jeju_free_shipping_fee, shipping_fee_presets')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) { logError('getShippingConfig', error); throw error; }
  return {
    shippingFee: data?.shipping_fee ?? null,
    freeThreshold: data?.free_shipping_threshold ?? null,
    jejuFreeShippingFee: data?.jeju_free_shipping_fee ?? null,
    shippingFeePresets: Array.isArray(data?.shipping_fee_presets) ? data.shipping_fee_presets : [],
  };
}

// confirm_deposit_match 직접 호출은 제거 — 모든 수동확정은 linkMatch(link_deposit_manual)로 통일(확정+다리학습+undo계약).
// confirm_deposit_match RPC 자체는 link_deposit_manual이 내부 위임으로만 사용(클라 EXECUTE 권한 revoke됨).

// ★expectedTotal이 null이면 price/override도 null(지문은 임계값과 세트 — codexloop 분할 R2).
// ⚠settingsFingerprint deprecated(2026-07-27, 마이그 20260727220000): undo RPC는 이 값을 보지 않는다.
//   자격 판정에서 지문 비교를 뺐다(§3.3 반전). 시그니처 호환 때문에 전달만 유지 — 검증 수단이 아니다.
export async function undoMatch(
  depositMatchId: number,
  expectedTotal: number | null,
  expectedPrice: number | null,
  expectedShipOverride: number | null,
  settingsFingerprint: string | null,
): Promise<DepositMatch> {
  const { data, error } = await supabase.rpc('undo_deposit_match', {
    p_deposit_match_id: depositMatchId,
    p_expected_total: expectedTotal,
    p_expected_price: expectedTotal === null ? null : expectedPrice,
    p_expected_ship_override: expectedTotal === null ? null : expectedShipOverride,
    p_settings_fingerprint: settingsFingerprint,
  });
  if (error) { logError('undoMatch', error); throw error; }
  return data as DepositMatch;
}

// ✗(무시/제외): 미확정 후보를 seller_rejected로 영속 마킹 → incoming 카드/칩에서 빠지고
// /deposits '제외됨' 섹션으로 간다. 확정된 매칭은 거부 불가(서버가 거절 — undo를 쓸 것).
export async function rejectMatch(depositMatchId: number): Promise<DepositMatch> {
  const { data, error } = await supabase.rpc('reject_deposit_match', {
    p_deposit_match_id: depositMatchId,
  });
  if (error) { logError('rejectMatch', error); throw error; }
  return data as DepositMatch;
}

// 제외 복구: seller_rejected 마커를 풀어(match_reason='restored' + 후보 비움) 다음 큐 로드에서 재도출되게 한다.
// 'restored'는 byAmount(amount_suggested) 분기를 안 타므로 byName-only로 재도출(무관 금액일치 오확정 차단).
// 미확정·seller_rejected 행에만 적용(서버가 그 외 거절). '제외됨' → '확인 대기'로 되돌리는 경로.
export async function restoreMatch(depositMatchId: number): Promise<DepositMatch> {
  const { data, error } = await supabase.rpc('restore_deposit_match', {
    p_deposit_match_id: depositMatchId,
  });
  if (error) { logError('restoreMatch', error); throw error; }
  return data as DepositMatch;
}

// 단계3 수동연결: 매칭 확정 + 다리 학습(입금자실명→닉네임)을 한 RPC로. confirmMatch의 superset
// (1~4단계 동일 계약 + bridge insert). 다음부터 같은 실명은 자동매칭. 학습은 서버 도출(클라 불신).
export async function linkMatch(
  depositMatchId: number, orderId: number, expectedModifiedAt: string, expectedTotal: number,
  expectedPrice: number, expectedShipOverride: number | null,
  source: 'order' | 'deposits' | 'deposits_rescue',
  settingsFingerprint: string | null,
): Promise<DepositMatch> {
  // ★expectedModifiedAt 필수(codexloop 제외 R4): 모든 확정 경로(Home=card.version, /deposits=match.modified_at)가
  //   버전을 넘겨 크로스탭 stale 확정을 차단한다. 타입을 required로 고정해 미래 호출자의 누락을 컴파일 단계에서 막는다.
  //   RPC는 restored 행에 버전 필수, 그 외엔 제공 시 stale 비교(미제공 허용 — legacy 계약 보존, 단 앱은 항상 전달).
  // ★expectedTotal도 required(동일 선례) — 분할입금 완납 임계값을 RPC가 서버 진실로 검증(p_expected_total).
  // ★expectedPrice/expectedShipOverride(codexloop 분할 R2): 금융 지문 — 로드 후 주문 금액/배송비오버라이드가
  //   편집됐으면 RPC가 40001로 거부(기존 isStaleDepositError 경로가 자동 처리).
  // ★source도 required(expectedModifiedAt 선례) — 확정 화면 계측('order'=정산서 카드, 'deposits'=/deposits).
  //   RPC는 DEFAULT NULL(구 클라 하위호환)이지만 앱은 항상 전달해 confirmed_source 공백을 남기지 않는다.
  // ⚠settingsFingerprint deprecated(2026-07-27, 마이그 20260727220000): confirm RPC는 이 값을 보지 않는다.
  //   자격 판정에서 지문 비교를 뺐다(§3.3 반전). rescue일 때 null을 넘기던 §3.6 배제도 이제 무동작이다.
  //   시그니처 호환 때문에 전달만 유지 — 검증 수단이 아니다. (전환 RPC convertPaidToPartial의 지문은 유효)
  const { data, error } = await supabase.rpc('link_deposit_manual', {
    p_deposit_match_id: depositMatchId,
    p_order_id: orderId,
    p_expected_modified_at: expectedModifiedAt,
    p_expected_total: expectedTotal,
    p_expected_price: expectedPrice,
    p_expected_ship_override: expectedShipOverride,
    p_source: source,
    p_settings_fingerprint: settingsFingerprint,
  });
  if (error) { logError('linkMatch', error); throw error; }
  return data as DepositMatch;
}

// ── 배송비 구제 CAS UPDATE 2종(스펙 §5, PR2) ─────────────────────────────────
// 구제 확정 체인은 서버 무변경(§2)이라 Step 2(배송비 기록)·undo 원복을 클라 CAS로 처리한다.
// order 테이블 접근은 이 deposit 레포가 이미 하는 일(findOrdersByIds/fetchUnassignedOrdersRaw)과 동일 —
// 페이지가 직접 supabase를 만지지 않고 service→repository 경유(레포 규율 준수). RLS(UPDATE=auth.uid()=user_id)가
// 소유권을 강제하므로 CAS는 스펙 §5의 조건 그대로만 건다(user_id 필터 불필요 — 쓰기 RLS가 타셀러 차단).

// Step 2(스펙 §5.4): 확정 성공 후 shipping_fee_override를 강화 CAS로 기록.
// override IS NULL ∧ 입금 완료 ∧ total_price=관측값일 때만 SET → Step1↔2 사이 undo·금액수정 개입 시 affected 0.
// 반환 true = 1행 반영, false = affected 0(경고 대상). 에러는 throw(호출측이 경고로 처리).
export async function applyRescueShippingOverride(
  orderId: number, presetAmount: number, observedTotal: number, matchId: number,
): Promise<boolean> {
  // Step 2 직전 match 재확인(PR 봇 P2 수용): Step 1 성공 ↔ 이 UPDATE 사이에 다른 탭이 undo 후
  // '다른' 입금을 같은 주문에 재확정하면 order 술어(override/status/total)만으론 통과해버린다 —
  // 우리 match가 여전히 이 주문의 deposits_rescue 확정일 때만 쓴다(잔여 TOCTOU는 ms 단위·표시 전용).
  const { data: m, error: mErr } = await supabase
    .from('deposit_match')
    .select('id, matched_order_id, confirmed_source, confirmed_at')
    .eq('id', matchId)
    .maybeSingle();
  if (mErr) { logError('applyRescueShippingOverride(match check)', mErr); throw mErr; }
  if (!m || m.matched_order_id !== orderId || m.confirmed_source !== 'deposits_rescue' || m.confirmed_at == null) {
    return false; // affected-0과 동일 의미 → 호출측 경고 토스트 경로
  }
  const { data, error } = await supabase
    .from('order')
    .update({ shipping_fee_override: presetAmount })
    .eq('id', orderId)
    .is('shipping_fee_override', null)
    .eq('status', '입금 완료')
    .eq('total_price', observedTotal)
    .select('id');
  if (error) { logError('applyRescueShippingOverride', error); throw error; }
  return (data ?? []).length > 0;
}

// undo 원복(스펙 §5): pre-undo 소스가 'deposits_rescue'였던 주문의 override를 CAS로 원복.
// override=preset ∧ 미지정일 때만 NULL로 → 동시 재확정·수동 변경(status≠미지정 or override≠preset) 시 affected 0.
// 반환 true = 1행 원복, false = affected 0(조용히 통과). 에러는 throw(호출측이 재조회 후 잔존 시에만 경고).
export async function revertRescueShippingOverride(
  orderId: number, presetAmount: number,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('order')
    .update({ shipping_fee_override: null })
    .eq('id', orderId)
    .eq('shipping_fee_override', presetAmount)
    .eq('status', '미지정')
    .select('id');
  if (error) { logError('revertRescueShippingOverride', error); throw error; }
  return (data ?? []).length > 0;
}

// undo 원복 CAS가 '실제 에러'로 실패한 뒤 잔존 확인(스펙 §5): 여전히 미지정 ∧ override=preset이면
// 같은 입금이 blue 정확일치로 재등장하는 피드백 루프 → 경고 대상. 조회 실패 = false(불확실 → 노이즈 회피).
export async function isRescueOverrideStuck(orderId: number, presetAmount: number): Promise<boolean> {
  const { data, error } = await supabase
    .from('order')
    .select('status, shipping_fee_override')
    .eq('id', orderId)
    .maybeSingle();
  if (error) { logError('isRescueOverrideStuck', error); return false; }
  return data?.status === '미지정' && data?.shipping_fee_override === presetAmount;
}

// 미매칭 입금 수동연결 picker용: 입금일 윈도우(-14~+1) 내 미지정 주문 전부(금액필터 없음 — UI 유력 배지).
// 매처 후보 윈도우와 동일 기준. ★user_id 명시 필터(findUnassignedOrdersInWindow 재사용 = 동일 교차노출 방어).
export async function findRecentUnassignedOrders(
  fromDate: string, toDate: string, config: ShippingConfig, userId: string
): Promise<OrderCandidate[]> {
  return findUnassignedOrdersInWindow(fromDate, toDate, config, userId);
}

// 이 셀러의 deposit_match 변경(INSERT=새 입금 매칭, UPDATE=확정 등)을 실시간 구독.
// onChange가 호출되면 호출측이 큐를 재조회한다. 반환값 = 구독 해제 함수(effect cleanup용).
// RLS가 본인 행으로 스코프하지만 채널 필터로도 user_id 한정(불필요한 이벤트 차단).
// onStatus: 채널 상태 콜백(CHANNEL_ERROR/TIMED_OUT 등 — 호출측이 로깅).
export function subscribeToDepositMatches(
  userId: string,
  onChange: () => void,
  onStatus?: (status: string, err?: Error) => void
): () => void {
  const channel = supabase
    .channel(`deposit-match-${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'deposit_match', filter: `user_id=eq.${userId}` },
      () => onChange()
    )
    .subscribe((status, err) => { onStatus?.(status, err); });
  return () => { supabase.removeChannel(channel); };
}

// nav 배지 전용 구독(증분2 D6 + codexloop R2 #1): deposit_match(카운트 갱신) +
// seller_deposit_account(연동 해제/변경 → 계정상태 재판정)를 한 채널로 구독.
// 채널명이 달라 useDepositIncoming의 deposit-match 채널과 topic 충돌 없음.
// seller_deposit_account는 20260706151000 마이그레이션으로 realtime publication에 추가됨.
export function subscribeToDepositBadge(
  userId: string,
  onMatchChange: () => void,
  onAccountChange: () => void,
  onStatus?: (status: string, err?: Error) => void
): () => void {
  const channel = supabase
    .channel(`deposit-badge-${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'deposit_match', filter: `user_id=eq.${userId}` },
      () => onMatchChange()
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'seller_deposit_account', filter: `user_id=eq.${userId}` },
      () => onAccountChange()
    )
    .subscribe((status, err) => { onStatus?.(status, err); });
  return () => { supabase.removeChannel(channel); };
}

// nav 배지용 확인 대기 카운트(증분2 D3) — loadDepositQueue의 actionNeeded 분류와 동일 조건.
// match_reason은 NOT NULL(phase3a L64)이라 단순 neq 안전. head:true = 행 전송 없는 경량 count.
// ★user_id 명시 필터 = 다른 deposit 쿼리와 동일 교차노출 방어. 실패는 null(배지만 숨김).
export async function countActionNeeded(userId: string): Promise<number | null> {
  const { count, error } = await supabase
    .from('deposit_match')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .is('confirmed_at', null)
    .neq('match_reason', 'seller_rejected');
  if (error) { logError('countActionNeeded', error); return null; }
  return count ?? null;
}

// 입금자실명(정규화) → nickname 집합. parsed_order(name) ∪ deposit_name_bridge(depositor_name).
// process-deposit의 union과 동일 소스/정규화 — 단계4 amber 분류가 자동매칭과 같은 이름지식을 쓰게.
// 입금자실명(정규화) → nickname 집합. parsed_order(name) ∪ deposit_name_bridge(depositor_name).
// ★depositorNames로 스코프: 이번 큐의 입금자명에 해당하는 행만 로드 → 전체 parsed_order 스캔과
//   '셀러 전체' PostgREST 1000행 truncation 회피(큰 셀러 amber 비일관 방지) + Home 로드 비용 최소.
//   단, 이건 글로벌 truncation만 막는다 — 동명이인 흔한 이름 1개가 단독으로 >1000 parsed_order
//   행을 가지면 그 이름의 nickname '집합'이 per-name truncate될 수 있다(낮지만 실재: 그 이름이
//   여러 닉네임에 매핑되고 드문 닉네임이 1000행 이후에만 나올 때 → 잘못된 좁힘 가능). amber는
//   수동+금액경고 게이트라 MVP 비차단. 완전 해소는 distinct (normalized_name,nickname) RPC = 스펙 미룬 후속.
// ★amber는 선택적 표시 — parsed_order/bridge 로드 실패해도 throw 안 하고 degrade(빈/부분 맵).
//   loadDepositIncoming/큐의 기존 matched·amount_suggested 카드가 이 맵 실패로 깨지면 안 됨(무회귀).
// ★주의: raw `name` exact IN 매칭이라 normalizeName으로만 같아지는 띄어쓰기 변형은 놓칠 수 있음
//   (amber가 보수적으로 미표시 → 해당 입금은 unmatched 유지 = 무해, 오연결 아님).
export async function getDepositorNicknameMap(userId: string, depositorNames: string[]): Promise<Map<string, Set<string>>> {
  const map = new Map<string, Set<string>>();
  if (depositorNames.length === 0) return map;   // 분류할 입금자명 없음 → 빈 맵(amber off)
  const add = (rawName: string | null, nickname: string | null) => {
    if (rawName == null || nickname == null) return;
    const key = normalizeName(rawName);
    if (!key) return;
    let set = map.get(key);
    if (!set) { set = new Set<string>(); map.set(key, set); }
    set.add(nickname);
  };

  const { data: poRows, error: poErr } = await supabase
    .from('parsed_order')
    .select('name, nickname')
    .eq('user_id', userId)
    .in('name', depositorNames);
  if (poErr) { logError('getDepositorNicknameMap.parsed_order', poErr); }  // degrade(throw 안 함)
  else for (const r of (poRows ?? []) as { name: string | null; nickname: string | null }[]) add(r.name, r.nickname);

  // bridge는 부가 학습소스 — 로드 실패해도 parsed_order만으로 진행(엣지 degrade 철학 동일).
  const { data: brRows, error: brErr } = await supabase
    .from('deposit_name_bridge')
    .select('depositor_name, nickname')
    .eq('user_id', userId)
    .in('depositor_name', depositorNames);
  if (brErr) { logError('getDepositorNicknameMap.bridge', brErr); }
  else for (const r of (brRows ?? []) as { depositor_name: string | null; nickname: string | null }[]) add(r.depositor_name, r.nickname);

  return map;
}

// 찜(추천 고정) 저장 — best-effort 보조 기록. 실패는 로깅만, throw 금지: 큐 로드의
// fire-and-forget 부수효과라 사용자 플로우를 막으면 안 되고, 다음 로드에서 같은 추천이
// 재도출되면 자연 재시도된다. RPC도 가드 미충족 시 예외 없이 false(스펙 §3.2).
export async function pinDepositCandidates(
  depositMatchId: number, orderId: number, expectedModifiedAt: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('pin_deposit_candidates', {
    p_deposit_match_id: depositMatchId,
    p_order_id: orderId,
    p_expected_modified_at: expectedModifiedAt,
  });
  if (error) { logError('pinDepositCandidates', error); return false; }
  return data === true;
}

// stale 찜 교체 — 이름모순/죽은 찜을 새 확신 추천으로 한 트랜잭션에서 X→Y 교체(codexloop R4).
// pin과 동일한 best-effort no-throw 계약. expectedOrderId = 배열 CAS(정확히 그 후보일 때만, ABA
// 차단). 빈 배열 중간상태가 없어 지연 pin의 옛 후보 부활도 원천 차단. RPC가
// match_status='unmatched'만 교체해 엣지 저장 후보(matched/ambiguous)는 절대 안 건드린다.
export async function replaceDepositCandidates(
  depositMatchId: number, expectedOrderId: number, newOrderId: number, expectedModifiedAt: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('replace_deposit_candidates', {
    p_deposit_match_id: depositMatchId,
    p_expected_order_id: expectedOrderId,
    p_new_order_id: newOrderId,
    p_expected_modified_at: expectedModifiedAt,
  });
  if (error) { logError('replaceDepositCandidates', error); return false; }
  return data === true;
}

// 참고 후보 풀(-60일) 전용 로더 — 스펙 §8. 기존 findUnassignedOrdersInWindow와 분리해
// ①합산(getConfirmedDepositSums) degrade가 기존 -14 경로에 전파되지 않게 하고
// ②주문 쿼리 truncation(1000행 캡) 의심 시 null로 참고만 fail-closed 한다(기존 등급 무영향).
// no-throw 계약: 참고는 보충 표시라 실패해도 큐 로드를 막으면 안 된다(로깅만).
export async function findReferenceOrdersInWindow(
  fromDate: string, toDate: string, config: ShippingConfig, userId: string
): Promise<OrderCandidate[] | null> {
  try {
    const { candidates, rawCount } = await fetchUnassignedOrdersRaw(fromDate, toDate, config, userId);
    // PostgREST 기본 row cap(1000) — 캡 감지는 필터 전(post-filter 아닌) 원본 행 수(rawCount) 기준.
    // candidates.length(부분 입금 잔액≤0/degrade 제외 후)를 쓰면, 원본이 정확히 1000행 캡에 걸려도
    // 필터로 <1000까지 줄어든 경우 truncation을 놓친다 — §8 fail-closed 취지가 무력화되는 갭이었다
    // (codexloop 구현 R1 F3).
    if (rawCount >= 1000) {
      logError('findReferenceOrdersInWindow.truncated',
        new Error(`possible 1000-row cap truncation (${fromDate}~${toDate})`));
      return null;
    }
    return candidates;
  } catch (err) {
    logError('findReferenceOrdersInWindow', err);
    return null;
  }
}

export interface PartialDepositOverviewRaw {
  order_id: number; nickname: string; order_date: string; total_price: number; status: string;
  shipping_fee_override: number | null; paid_total_snapshot: number | null; paid_at: string | null;
  confirmed_sum: number; paid_threshold: number; remaining: number; is_safety_lane: boolean;
  item_names: string[]; candidate_count: number; settings_fingerprint: string | null;
}

const OVERVIEW_PAGE_LIMIT = 500;
const OVERVIEW_MAX_PAGES = 8;                      // 데이터 페이지 안전캡(4,000행) — 소진 시 null(fail-closed)

// torn: 페이지 간 서버 지문 변동(페이지네이션 중 배송설정 변경) — 서로 다른 설정 세대의 임계값이
// 한 결과에 섞였다는 신호. 호출측(훅)이 1회 재조회, 2회 연속이면 fail-closed (PR C 지문 재시도 계약 보존 —
// 비교 축만 서버-vs-클라에서 페이지-간으로 이동: 임계값이 DB 단일 소스가 되며 클라 config 로드 자체가 사라짐 §16.3).
export async function fetchPartialDepositOverview(): Promise<{
  rows: PartialDepositOverviewRow[]; serverFingerprint: string | null; torn: boolean;
} | null> {
  const rows: PartialDepositOverviewRow[] = [];
  let cursor: number | null = null;
  let serverFingerprint: string | null = null;
  // 첫 데이터 페이지 관측 여부 — null도 유효한 지문 값(member_setting 無)이라 "미초기화"와 구분해야
  // null→non-null 세대 변화(페이지네이션 중 설정 첫 저장)를 놓치지 않는다.
  let hasSeenPage = false;
  // <=: 데이터 페이지 8회 + 빈 sentinel 1회 = 최대 9요청 — sentinel이 캡을 소비해
  // 수용치가 4,000→3,500행으로 줄지 않도록 (codexloop PR C R2)
  for (let page = 0; page <= OVERVIEW_MAX_PAGES; page++) {
    const { data, error } = await supabase.rpc('get_partial_deposit_overview', {
      p_cursor_id: cursor, p_limit: OVERVIEW_PAGE_LIMIT,
    });
    if (error) throw error;
    const batch = (data ?? []) as PartialDepositOverviewRaw[];
    // 종료 = 빈 페이지 sentinel 단독 — 서버 LEAST 캡·candidate_count에 비의존.
    // batch.length < limit(서버 캡 하향 시 조용한 truncation)·candidate_count 도달(페이지 간
    // 스냅샷 드리프트 시 마지막 행 누락) 종료는 codexloop PR C R1-P1-3/A8로 제거.
    if (batch.length === 0) return { rows, serverFingerprint, torn: false };
    if (hasSeenPage && batch[0].settings_fingerprint !== serverFingerprint) {
      return { rows: [], serverFingerprint: null, torn: true };
    }
    hasSeenPage = true;
    serverFingerprint = batch[0].settings_fingerprint;
    for (const r of batch) {
      rows.push({
        orderId: r.order_id, nickname: r.nickname, orderDate: r.order_date, totalPrice: r.total_price,
        status: r.status, shippingFeeOverride: r.shipping_fee_override,
        paidTotalSnapshot: r.paid_total_snapshot, paidAt: r.paid_at,
        confirmedSum: r.confirmed_sum, paidThreshold: r.paid_threshold, remaining: r.remaining,
        isSafetyLane: r.is_safety_lane, itemNames: r.item_names ?? [],
      });
    }
    cursor = batch[batch.length - 1].order_id;
  }
  return null;  // 안전캡 초과 = fail-closed (조용한 누락 금지, 스펙 §4.3)
}

// P0002/P0003 + DETAIL(reason 토큰) → typed result (스펙 §5 계약. 그 외 에러는 재throw)
function mapConvertError(error: unknown): { ok: false; reason: string } | null {
  const e = error as { code?: string; details?: string } | null;
  if (e && (e.code === 'P0002' || e.code === 'P0003')) {
    return { ok: false, reason: e.details && e.details.length > 0 ? e.details : 'stale' };
  }
  return null;
}

export async function convertPaidOrderToPartial(
  orderId: number, expectedTotal: number, expectedShipOverride: number | null,
  expectedThreshold: number, settingsFingerprint: string,
): Promise<{ ok: boolean; reason?: string }> {
  const { data, error } = await supabase.rpc('convert_paid_order_to_partial', {
    p_order_id: orderId, p_expected_total: expectedTotal, p_expected_ship_override: expectedShipOverride,
    p_expected_threshold: expectedThreshold, p_settings_fingerprint: settingsFingerprint,
  });
  if (error) {
    const mapped = mapConvertError(error);
    if (mapped) return mapped;
    throw error;
  }
  return data as { ok: true };
}

// settle_partial_order(스펙 §16.7) — up-manual 마감. 미달 + ack=false는 예외가 아니라
// 구조화 반환({ok:false, reason:'requires_manual_override', ...}) → UI 확인 다이얼로그 → ack 재시도.
// expectedSnapshot = 화면이 본 스냅샷 세대 CAS(IS DISTINCT FROM — 네이티브 부분입금 NULL 허용).
export interface SettlePartialOrderResult {
  ok: boolean; reason?: string; confirmedSum?: number; paidThreshold?: number;
}

export async function settlePartialOrder(
  orderId: number, expectedTotal: number, expectedShipOverride: number | null,
  expectedSnapshot: number | null, ackUnderpaid: boolean,
): Promise<SettlePartialOrderResult> {
  const { data, error } = await supabase.rpc('settle_partial_order', {
    p_order_id: orderId, p_expected_total: expectedTotal, p_expected_ship_override: expectedShipOverride,
    p_expected_snapshot: expectedSnapshot, p_ack_underpaid: ackUnderpaid,
  });
  if (error) {
    const mapped = mapConvertError(error);
    if (mapped) return mapped;
    throw error;
  }
  const d = data as { ok: boolean; reason?: string; confirmed_sum?: number; paid_threshold?: number };
  return d.ok
    ? { ok: true }
    : { ok: false, reason: d.reason, confirmedSum: d.confirmed_sum, paidThreshold: d.paid_threshold };
}
